import React, { useState } from "react";
import { partService } from "../Services/PartService";
import { vendorService } from "../Services/VendorService";
import { purchaserService } from "../Services/PurchaserService";
import { consigneeService } from "../Services/ConsigneeService";
import toast from "react-hot-toast";

interface DatabaseActionsProps {
  databaseType: "parts" | "vendors" | "purchasers" | "consignees";
  onRefresh: () => void;
}

interface LimitInfo {
  canAdd: boolean;
  currentCount: number;
  limit: number | string;
  tier: string;
  wouldExceedBy?: number;
}

interface ServerError {
  message: string;
  limitInfo?: LimitInfo;
  results?: {
    success: number;
    failed: number;
    errors: Array<{
      partNumber?: string;
      name?: string;
      error: string;
    }>;
  };
}

export const DatabaseActions: React.FC<DatabaseActionsProps> = ({
  databaseType,
  onRefresh,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Get the appropriate service based on database type
  const getService = () => {
    switch (databaseType) {
      case "parts":
        return partService;
      case "vendors":
        return vendorService;
      case "purchasers":
        return purchaserService;
      case "consignees":
        return consigneeService;
      default:
        throw new Error(`Unknown database type: ${databaseType}`);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }

    setIsUploading(true);

    try {
      const service = getService();

      // Check if the service has an upload method
      if (!service.upload) {
        toast.error(`Upload not supported for ${databaseType} yet`);
        return;
      }

      const result = await service.upload(file);

      // Show success message with details
      if (result.results) {
        const { success, failed, errors } = result.results;

        if (success > 0 && failed === 0) {
          toast.success(`Successfully uploaded ${success} ${databaseType}!`);
        } else if (success > 0 && failed > 0) {
          toast.success(
            <div>
              <div className="font-medium">Upload completed with some issues</div>
              <div className="text-sm mt-1">
                ✅ {success} {databaseType} added successfully<br />
                ❌ {failed} {databaseType} failed
              </div>
            </div>,
            { duration: 6000 }
          );

          // Show detailed errors
          if (errors.length > 0) {
            const errorSummary = errors.slice(0, 3).map((err: any) => {
              const identifier = err.partNumber || err.name || 'Unknown';
              return `${identifier}: ${err.error}`;
            }).join('\n');

            toast.error(
              <div>
                <div className="font-medium">Upload Errors:</div>
                <div className="text-xs mt-1 whitespace-pre-line">
                  {errorSummary}
                  {errors.length > 3 && `\n... and ${errors.length - 3} more errors`}
                </div>
              </div>,
              { duration: 8000 }
            );
          }
        } else {
          toast.error(`Upload failed. ${failed} ${databaseType} could not be processed.`);
        }
      } else {
        toast.success(result.message || "Upload completed successfully!");
      }

      onRefresh();
    } catch (err: unknown) {
      console.error("Upload error:", err);

      // Type guard to check if err is an axios error
      const isAxiosError = (error: unknown): error is { response?: { status: number; data: ServerError } } => {
        return typeof error === 'object' && error !== null && 'response' in error;
      };

      // Handle different types of errors
      if (isAxiosError(err) && err.response?.status === 403) {
        // Subscription limit error
        const serverError: ServerError = err.response.data;
        if (serverError.limitInfo) {
          showLimitError(serverError.limitInfo);
        } else {
          toast.error(serverError.message || "Upload would exceed your subscription limit");
        }
      } else if (isAxiosError(err) && err.response?.status === 400) {
        // Validation error
        const errorMessage = err.response.data.message || "Invalid file or data format";
        toast.error(errorMessage);
      } else {
        // Generic error
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setIsUploading(false);
      // Reset the file input
      event.target.value = '';
    }
  };


  // Function to show subscription limit error with upgrade option
  const showLimitError = (limitInfo: LimitInfo) => {
    const limitText = limitInfo.limit === 'unlimited' ? 'unlimited' : limitInfo.limit.toString();

    toast.error(
      <div className="space-y-2">
        <div className="font-medium">Upload Limit Exceeded</div>
        <div className="text-sm">
          Your <span className="capitalize font-medium">{limitInfo.tier}</span> plan allows {limitText} {databaseType}.
          <br />
          You currently have {limitInfo.currentCount} {databaseType}.
          {limitInfo.wouldExceedBy && (
            <>
              <br />
              This upload would exceed your limit by {limitInfo.wouldExceedBy} {databaseType}.
            </>
          )}
        </div>
        <button
          onClick={() => {
            // Navigate to upgrade page or show upgrade modal
            window.location.href = '/account#subscription';
          }}
          className="mt-2 px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
        >
          Upgrade Plan
        </button>
      </div>,
      {
        duration: 10000,
        style: {
          maxWidth: '400px',
        }
      }
    );
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloading(true);
      const service = getService();

      // Check if the service has a downloadTemplate method
      if (!service.downloadTemplate) {
        toast.error(`Template download not supported for ${databaseType} yet`);
        return;
      }

      await service.downloadTemplate();
      toast.success("Template downloaded successfully!");
    } catch (err) {
      console.error("Download template error:", err);
      toast.error("Failed to download template. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    try {
      setIsDownloading(true);
      const service = getService();

      // Check if the service has a downloadAll method
      if (!service.downloadAll) {
        toast.error(`Export not supported for ${databaseType} yet`);
        return;
      }

      await service.downloadAll();
      toast.success(`${databaseType} exported successfully!`);
    } catch (err: any) {
      console.error("Download all error:", err);
      const errorMessage = err.response?.data?.message || `Failed to export ${databaseType}. Please try again.`;
      toast.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {/* Upload Button */}
      <div className="relative">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        <button
          disabled={isUploading}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isUploading && (
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {isUploading ? "Uploading..." : `Upload ${databaseType}`}
        </button>
      </div>

      {/* Download Template Button */}
      <button
        onClick={handleDownloadTemplate}
        disabled={isDownloading}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
      >
        {isDownloading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        Download Template
      </button>

      {/* Download All Button */}
      <button
        onClick={handleDownloadAll}
        disabled={isDownloading}
        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
      >
        {isDownloading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        Export All {databaseType}
      </button>

      {/* Refresh Button */}
      <button
        onClick={onRefresh}
        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
      >
        Refresh
      </button>
    </div>
  );
};
